/**
 * AuthShell — the split brand/form pane from
 * docs/html-prototypes/CAR-IA/car-ia-login.html, shared by Login and Register.
 *
 * Left: dark gradient brand pane with the value bullets (hidden ≤880px, where
 * the compact `mbrand` takes over). Right: the form column, max 380px.
 * The Ingresar / Crear cuenta tabs are real navigation here — the prototype
 * swaps modes in-page, but the app keeps /login and /register as routes.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

import './auth.css';
import { LockIcon, ScaleIcon, ShieldTrustIcon } from '../../../commodity/components/caria';

const BULLETS: [React.FC<{ size?: number; strokeWidth?: number }>, string][] = [
  [ShieldTrustIcon, 'Vendedores verificados (KYC) en cada anuncio'],
  [ScaleIcon, 'Tasación certificada y rango de mercado en tiempo real'],
  [LockIcon, 'Pagos protegidos hasta que recibes el vehículo'],
];

const Brand: React.FC = () => (
  <span className="cia-auth-brand">
    CAR<b>-IA</b>
  </span>
);

export interface AuthShellProps {
  /** Which tab reads as active. */
  tab: 'login' | 'register';
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

export const AuthShell: React.FC<AuthShellProps> = ({ tab, title, subtitle, children }) => {
  const navigate = useNavigate();

  return (
    <div className="cia-auth">
      <aside className="cia-auth-brandpane">
        <button className="cia-auth-brand" onClick={() => navigate('/')} aria-label="Inicio">
          CAR<b>-IA</b>
        </button>
        <div>
          <h1>Compra y vende con confianza, respaldado por datos.</h1>
          <p>
            Miles de dominicanos ya usan CAR-IA para encontrar su próximo vehículo con tasación
            certificada y verificación real del vendedor.
          </p>
          <div className="cia-auth-blist">
            {BULLETS.map(([Icon, text]) => (
              <div key={text} className="cia-auth-bitem">
                <Icon size={18} strokeWidth={1.8} />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="cia-auth-bfoot">© 2026 CAR-IA · Gratis para todos</p>
      </aside>

      <main className="cia-auth-formpane">
        <div className="cia-auth-formbox">
          <button className="cia-auth-mbrand" onClick={() => navigate('/')} aria-label="Inicio">
            <Brand />
          </button>

          <div className="cia-auth-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'login'}
              className={tab === 'login' ? 'on' : undefined}
              onClick={() => navigate('/login')}
            >
              Ingresar
            </button>
            <button
              role="tab"
              aria-selected={tab === 'register'}
              className={tab === 'register' ? 'on' : undefined}
              onClick={() => navigate('/register')}
            >
              Crear cuenta
            </button>
          </div>

          <h2>{title}</h2>
          <p className="cia-auth-sub">{subtitle}</p>

          {children}
        </div>
      </main>
    </div>
  );
};

/** Show / hide toggle for a password input, matching the prototype's field. */
export const PasswordField: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  autoComplete?: string;
  testId?: string;
}> = ({ id, label, value, onChange, error, autoComplete = 'current-password', testId }) => {
  const [shown, setShown] = React.useState(false);
  return (
    <div className="cia-auth-fld">
      <label htmlFor={id}>{label}</label>
      <div className="cia-auth-pw">
        <input
          id={id}
          name={id}
          type={shown ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete}
          aria-invalid={error ? 'true' : undefined}
          data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          aria-label={shown ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          onClick={() => setShown((s) => !s)}
        >
          {shown ? (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8" />
              <path d="M9.4 5.2A9.4 9.4 0 0112 5c5 0 9 4.5 9 7a12 12 0 01-2.4 3.3M6.2 6.7C3.9 8.2 3 10.4 3 12c0 2.5 4 7 9 7 1.2 0 2.3-.2 3.3-.6" />
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12s3.6-7 9-7 9 7 9 7-3.6 7-9 7-9-7-9-7z" />
              <circle cx="12" cy="12" r="2.6" />
            </svg>
          )}
        </button>
      </div>
      {error && <span className="cia-auth-fld-err">{error}</span>}
    </div>
  );
};

/** Plain labelled text input in the prototype's field style. */
export const Field: React.FC<{
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  autoComplete?: string;
  placeholder?: string;
  testId?: string;
}> = ({ id, label, type = 'text', value, onChange, error, autoComplete, placeholder, testId }) => (
  <div className="cia-auth-fld">
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      name={id}
      type={type}
      value={value}
      autoComplete={autoComplete}
      placeholder={placeholder}
      aria-invalid={error ? 'true' : undefined}
      data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && <span className="cia-auth-fld-err">{error}</span>}
  </div>
);

export default AuthShell;
